import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import { withRouter } from "react-router";
import { useI18n } from "Hooks";
import ACLStore from "lib/aclStore";
import classes from "./ToddleDoc.scss";
import { colors } from "Constants";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { getFileType } from "Utils";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import Loading from "UIComponents/Loading";
import DocEditorWrapper from "UIComponents/DocEditor/DocEditorWrapper";
import ToddleDocHeader from "./components/ToddleDocHeader";
import ToddleDocNameModal from "./components/ToddleDocNameModal";
import UIModal from "UIComponents/UIModal/UIModal";
import { updateAttachment } from "OrganizationResources/modules/OrganizationResourcesModule";
import { getToddleDocAttachmentQuery } from "./Modules/ToddleDocQuery";
import {
  createToddleDocConversation,
  updateToddleDocConversation,
  deleteToddleDocConversation,
} from "./Modules/ToddleDocModule";
import {
  mapBackendConversationsToFrontend,
  CREATE,
  EDIT,
  VIEW,
  ADD_COMMENT,
  DELETE_COMMENT,
  UPDATE_COMMENT,
  RESOLVE_CONVERSATION,
  ACCEPT_SUGGESTION,
  UPDATE_CONVERSATION,
  ADD_DRAFT_CONVERSATION,
  generateConversationsHash,
  getLabValueMap,
  getFilterDropdownOptions,
} from "./Modules/utils";
import {
  Dropdown,
  DropdownMenu,
  DSProvider,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import { updateEvaluationData } from "ClassRoom/modules/ClassRoomModule";
import { setToastMsg } from "routes/modules/LoginModule/LoginActions";

const BLANK_OBJECT = {};
const styles = {
  content: {
    color: colors.neutral400,
    fontSize: "1.2rem",
  },
  docEditorContainerStyles: {
    width: "100%",
    height: "calc(100vh - 64px)",
  },
  contentBgProvider: {
    padding: "0 32px",
    margin: "32px 16px",
    border: "1px solid var(--border-secondary)",
    borderRadius: "8px",
  },
  anchorElement: {
    maxWidth: "900px",
  },
  contentBgProviderV2: {
    margin: "32px 16px",
    border: "1px solid var(--border-secondary)",
    borderRadius: "8px",
  },
  contentBgProviderTransitionsRemoved: {
    transform: "none",
  },
  contentEditable: {
    padding: "32px 50px",
  },
  smallerWidthContent: {
    padding: "0px",
    margin: "0px",
    borderRadius: "0px",
    border: "none",
  },
};

const DEFAULT_EDITOR_STYLES = {
  docEditorStyles: {
    contentBgProvider: styles.contentBgProviderV2,
    contentEditable: styles.contentEditable,
  },
};

const nodeConfig = {
  image: {
    comments: true,
  },
};

const disabledNodes = { embed: true };
const TABLE_MAXWIDTH = 796;
const EMPTY_P_REGEX = /^(?:<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+$/;
const DEFAULT_COMMENT_FILTERS = "ACTIVE";
const DEFAULT_DOC_EDITOR_CONFIG = {
  toolbar: {
    AI: {
      isEnabled: false,
    },
    comments: {
      isEnabled: true,
    },
  },
  floatingToolbar: {
    AI: {
      isEnabled: false,
    },
    comments: {
      isEnabled: true,
    },
  },
};

const DocEditorWithMemo = memo(
  props => {
    return <DocEditorWrapper {...props} />;
  },
  (prevProps, nextProps) => {
    if (nextProps.instanceId) {
      return (
        prevProps.commentUpdateId === nextProps.commentUpdateId &&
        prevProps.commentListType === nextProps.commentListType &&
        prevProps.value === nextProps.value &&
        prevProps.waitForCommentsToLoad === nextProps.waitForCommentsToLoad &&
        prevProps.instanceId === nextProps.instanceId &&
        prevProps.extendedToolbarButtons === nextProps.extendedToolbarButtons &&
        prevProps.width === nextProps.width &&
        prevProps.viewOnly === nextProps.viewOnly &&
        prevProps.config === nextProps.config &&
        JSON.stringify(prevProps.docEditorStyles) ===
          JSON.stringify(nextProps.docEditorStyles) &&
        JSON.stringify(prevProps.comments) ===
          JSON.stringify(nextProps.comments)
      );
    }
  }
);

DocEditorWithMemo.displayName = "DocEditorWithMemo";

const ToddleDocComponent = props => {
  const {
    onClose,
    updateAttachment,
    isEvaluation = false,
    attachment, // pass value of attachment from parent and do not rely on fetchedAttachment
    onChangeAttachment, // callback to update the attachment value in parent component
    fetchedAttachment,
    isData,
    isLoading,
    fetchPolicy,
    mode = EDIT,
    createAttachment,
    userDetails,
    createToddleDocConversation,
    updateToddleDocConversation,
    deleteToddleDocConversation,
    conversations,
    showEditTitle = true,
    onSubmit,
    onBackClick,
    getToddleDocAttachmentDetails,
    showControlledEditMode = false,
    isInitialContentChangeIgnored,
    docEditorConfig = DEFAULT_DOC_EDITOR_CONFIG,
    containerStyle,
    nodesDisabled,
    defaultTagSizes,
    width,
    blockUpdates = false,
    nodeConfig: nodeCofigFromProps,
    isCommentView: isCommentViewFromProp,
    disableStaffPublish = false,
    isAITutor = false,
    isAddWorkFromTeacher,
    updateEvaluationData,
    commentsState,
    showFiltersForEditMode,
    setToastMsg,
  } = props;
  const { t } = useI18n();
  const [instanceId, setInstanceId] = useState(attachment?.id || "");
  const [showNameModal, setShowNameModal] = useState(false);
  const [closeOnSubmit, setCloseOnSubmit] = useState(false);
  const [isCreateNewDoc, setIsCreateNewDoc] = useState(mode === CREATE);
  const [comments, setComments] = useState([]);
  const [isCommentsLoaded, setIsCommentsLoaded] = useState(false);
  const [waitForCommentsToLoad, setWaitForCommentsToLoad] = useState(true);
  const [isRenameClicked, setIsRenameClicked] = useState(false);
  const [calculatedWidth, setCalculatedWidth] = useState(
    isEvaluation ? 800 : 960
  );
  const [docEditorStyles, setDocEditorStyles] = useState({});
  const [derivedMode, setDerivedMode] = useState(
    showControlledEditMode && mode === EDIT ? VIEW : null
  );
  const [displayedCommentsLength, setDisplayedCommentsLength] = useState(0);
  const [commentFilters, setCommentFilters] = useState(DEFAULT_COMMENT_FILTERS);

  const commentsRef = useRef(new Map());
  const toddleDocConfig = useRef();
  const docWrapperRef = useRef(null);
  const attachmentInfoRef = useRef({});
  const prevConversationsHashRef = useRef("");
  const isLocalUpdateRef = useRef(false);
  const pendingUpdatesRef = useRef(new Set());

  const viewOnly = mode === VIEW;
  const isCommentView = _.isBoolean(isCommentViewFromProp)
    ? isCommentViewFromProp
    : mode === VIEW || derivedMode === VIEW;
  const isCommentViewAndNewUIEnabled = ACLStore.can(
    "FeatureFlag:ToddleDocComments"
  );

  const showLoading = isAITutor
    ? fetchPolicy === "network-only"
      ? isLoading
      : isLoading && !isData
    : isLoading;

  const {
    toddleDocId,
    attachmentId,
    attachmentValue,
    attachmentName,
  } = useMemo(() => {
    const src = { ...fetchedAttachment, ...attachment };

    return {
      toddleDocId: src.toddleDoc?.id,
      attachmentName: src.name,
      attachmentId: src.attachmentId ?? src.id ?? "",
      attachmentValue: src.toddleDoc?.value ?? src.modalityAttachment?.content,
    };
  }, [attachment, fetchedAttachment]);

  useEffect(() => {
    if (isCreateNewDoc && _.isFunction(createAttachment)) {
      createAttachment({ name: "Untitled document" });
      setIsCreateNewDoc(false);
    }
  }, [isCreateNewDoc, createAttachment]);

  // to prevent closure
  useEffect(() => {
    attachmentInfoRef.current = {
      toddleDocId,
      attachmentId,
      attachmentValue,
      attachmentName,
    };
  }, [toddleDocId, attachmentId, attachmentValue, attachmentName]);

  useLayoutEffect(() => {
    const PADDING = 40;
    const MAX_EVAL = 800;
    const MAX_DEFAULT = 960;

    const computeWidth = () => {
      const containerWidth = docWrapperRef.current
        ? docWrapperRef.current?.clientWidth
        : typeof window !== "undefined"
        ? window.innerWidth
        : null;

      if (!containerWidth) return;

      const maxLimit = Math.min(
        containerWidth,
        isEvaluation ? MAX_EVAL : MAX_DEFAULT
      );
      const newWidth = Math.max(200, Math.floor(maxLimit - PADDING));
      setCalculatedWidth(newWidth);
    };

    computeWidth();
    const debouncedCompute = _.debounce(computeWidth, 120);
    window.addEventListener("orientationchange", debouncedCompute);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && docWrapperRef.current) {
      resizeObserver = new ResizeObserver(debouncedCompute);
      resizeObserver.observe(docWrapperRef.current);
    }
    return () => {
      window.removeEventListener("resize", debouncedCompute);
      if (resizeObserver) resizeObserver.disconnect();
      if (debouncedCompute.cancel) debouncedCompute.cancel();
    };
  }, [isEvaluation]);

  useEffect(() => {
    toddleDocConfig.current = {
      toddleDocId,
      isStaffPublished:
        _.get(userDetails, "userType") === "staff" && !disableStaffPublish,
    };
  }, [toddleDocId, userDetails, isEvaluation, disableStaffPublish]);

  useEffect(() => {
    if (showLoading) {
      if (!isAITutor) {
        setIsCommentsLoaded(false);
        setWaitForCommentsToLoad(true);
        setComments([]);
      }
      return;
    }
    setInstanceId(attachmentId);
    // Clear comments immediately when hide all is selected, without waiting for GraphQL refetch
    if (commentsState === "NONE") {
      commentsRef.current = new Map();
      setComments([]);
      setStyles({ commentsRef: new Map(), comments: [] });
      setIsCommentsLoaded(true);
      setWaitForCommentsToLoad(false);
      prevConversationsHashRef.current = "";
      return;
    }

    const conversationsHash = generateConversationsHash(conversations);

    //skip if useEffect is triggered due to local comment update.
    if (isLocalUpdateRef.current) {
      isLocalUpdateRef.current = false;
      return;
    }

    // Skip if hash is the same (data hasn't actually changed)
    if (prevConversationsHashRef.current === conversationsHash) {
      return;
    }

    prevConversationsHashRef.current = conversationsHash;
    const formatted = _.isArray(conversations) ? conversations : [];
    const { commentMap, filteredComments } = (() => {
      const commentMap = new Map();
      const filteredComments = [];

      _.forEach(formatted, item => {
        // Build comment map
        commentMap.set(String(item.internalThreadId), item);

        // Filter comments
        if (!_.isEmpty(item.comments)) {
          filteredComments.push(item);
        }
      });

      return { commentMap, filteredComments };
    })();

    commentsRef.current = commentMap;
    setComments(filteredComments);
    setStyles({ commentsRef: commentMap, comments: filteredComments });
    setIsCommentsLoaded(true);
    setWaitForCommentsToLoad(false);
  }, [conversations, showLoading, commentsState, attachmentId]);

  // Debounce style updates during filter changes
  const debouncedSetStyles = useCallback(
    _.debounce(({ commentsRef, comments, checkDisplayedCommentsLength }) => {
      const containerWidth =
        docWrapperRef.current?.clientWidth ??
        (typeof window !== "undefined" ? window.innerWidth : 0);
      const isWide = containerWidth >= 900;

      setDocEditorStyles(() => {
        if (isEvaluation) {
          return {
            docEditorStyles: {
              contentBgProvider: !isWide
                ? { ...styles.smallerWidthContent }
                : {
                    ...styles.contentBgProviderV2,
                    ...styles.contentBgProviderTransitionsRemoved,
                  },
              contentEditable: styles.contentEditable,
              anchorElement: styles.anchorElement,
            },
          };
        }
        return BLANK_OBJECT;
      });
    }, 300),
    [
      isEvaluation,
      viewOnly,
      docWrapperRef?.current?.clientWidth,
      displayedCommentsLength,
    ]
  );

  const setStyles = useCallback(
    ({
      commentsRef,
      comments = comments,
      checkDisplayedCommentsLength = false,
    }) => {
      debouncedSetStyles({
        commentsRef,
        comments,
        checkDisplayedCommentsLength,
      });
    },
    [debouncedSetStyles]
  );

  useEffect(() => {
    const commentMap = commentsRef.current;
    setStyles({
      commentsRef: commentMap,
      comments,
      checkDisplayedCommentsLength: true,
    });
  }, [
    docWrapperRef?.current?.clientWidth,
    comments,
    displayedCommentsLength,
    setStyles,
  ]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (debouncedSetStyles.cancel) {
        debouncedSetStyles.cancel();
      }
    };
  }, [debouncedSetStyles]);

  const handleCommentAction = useCallback(
    async (action, payload = {}, thread, onSuccess) => {
      const config = {
        isStaffPublished: _.get(toddleDocConfig.current, "isStaffPublished"),
        isAiSuggestionAccepted: action === ACCEPT_SUGGESTION,
      };

      const commentMap = commentsRef.current;
      const threadKey = _.get(thread, "internalThreadId");
      const existingConversation = commentMap.get(threadKey);
      const toddleDocIdValue = _.get(toddleDocConfig.current, "toddleDocId");

      const setUpdatedThread = ({
        conversation,
        baseThread = thread,
        isDeleted = false,
      }) => {
        const comments = _.get(conversation, "messages", []);
        const newCommentObject = _.get(comments, payload?.commentIndex);

        if (isDeleted) {
          commentMap.delete(threadKey);
        } else if (newCommentObject && _.isFunction(onSuccess)) {
          const newThreadInstance = onSuccess({
            internalCommentId: _.get(newCommentObject, "id"),
          });

          commentMap.set(
            threadKey,
            _.cloneDeep({
              ...newThreadInstance,
              id: _.get(conversation, "id"),
            })
          );
        } else {
          commentMap.set(
            threadKey,
            _.cloneDeep({
              ...baseThread,
              id: _.get(conversation, "id"),
            })
          );
        }
      };

      switch (action) {
        case ADD_DRAFT_CONVERSATION: {
          if (!_.isEmpty(existingConversation)) {
            isLocalUpdateRef.current = true;
            const updatedConversation = await updateToddleDocConversation(
              _.cloneDeep(existingConversation),
              _.cloneDeep(thread)
            );
            setUpdatedThread({ conversation: updatedConversation });
          } else {
            const createdConversation = await createToddleDocConversation(
              toddleDocIdValue,
              _.cloneDeep(thread),
              config
            );
            setUpdatedThread({ conversation: createdConversation });
          }
          break;
        }
        case DELETE_COMMENT:
        case UPDATE_CONVERSATION:
        case RESOLVE_CONVERSATION:
        case UPDATE_COMMENT:
        case ACCEPT_SUGGESTION:
        case ADD_COMMENT: {
          if (!_.isEmpty(existingConversation)) {
            if (_.get(thread, "isDeleted")) {
              await deleteToddleDocConversation(
                _.get(existingConversation, "id")
              );
              setUpdatedThread({ isDeleted: true });
              break;
            }
            isLocalUpdateRef.current = true;
            const updatedConversation = await updateToddleDocConversation(
              _.cloneDeep(existingConversation),
              _.cloneDeep(thread),
              action !== RESOLVE_CONVERSATION ? config : undefined
            );
            setUpdatedThread({ conversation: updatedConversation });
            if (action === RESOLVE_CONVERSATION) {
              setToastMsg({
                msg: "toastMsgs:comment_resolved",
                type: "success",
                position: "toast-bottom-right",
              });
            }
            return;
          }

          // No existing — create a new conversation
          const createdConversation = await createToddleDocConversation(
            toddleDocIdValue,
            _.cloneDeep(thread),
            config
          );
          setUpdatedThread({ conversation: createdConversation });
          break;
        }

        default:
          break;
      }
      setStyles?.({ commentsRef: commentsRef.current });
    },
    [
      attachmentId,
      deleteToddleDocConversation,
      updateToddleDocConversation,
      createToddleDocConversation,
      commentFilters,
      setToastMsg,
    ]
  );

  const commentHandlers = useMemo(
    () => ({
      onCommentAdd: (comm, thread, onSuccess) =>
        handleCommentAction(ADD_COMMENT, comm, thread, onSuccess),
      onCommentDelete: (comment, thread, onSuccess) =>
        handleCommentAction(DELETE_COMMENT, comment, thread, onSuccess),
      onCommentUpdate: (comment, thread, onSuccess) =>
        handleCommentAction(UPDATE_COMMENT, comment, thread, onSuccess),
      onCommentResolve: (thread, onSuccess) =>
        handleCommentAction(RESOLVE_CONVERSATION, {}, thread, onSuccess),
      onCommentSuggestionAccept: (thread, onSuccess) =>
        handleCommentAction(ACCEPT_SUGGESTION, {}, thread, onSuccess),
      onThreadUpdate: (thread, onSuccess) =>
        handleCommentAction(UPDATE_CONVERSATION, null, thread, onSuccess),
      onCommentDraftUpdate: (comment, thread, onSuccess) =>
        handleCommentAction(ADD_DRAFT_CONVERSATION, comment, thread, onSuccess),
    }),
    [handleCommentAction]
  );

  const isEmptyHtml = html => {
    if (
      !_.isString(html) ||
      _.isEmpty(_.trim(html)) ||
      (EMPTY_P_REGEX?.test(_.trim(html)) && (isCommentView || viewOnly))
    )
      return true;
    return false;
  };

  const handleUpdateValue = useCallback(
    (value, isEmpty = false) => {
      if (blockUpdates) return;
      if (attachmentId) {
        const input = {
          id: attachmentId,
          toddleDoc: { value },
        };

        if (onChangeAttachment) {
          onChangeAttachment(input, isEmpty);
        } else if (!isEmptyHtml(value)) {
          const promise = Promise.resolve(updateAttachment?.(input));
          pendingUpdatesRef.current?.add(promise);
          promise.finally(() => pendingUpdatesRef.current?.delete(promise));
        }
        toddleDocConfig.current = {
          ...toddleDocConfig.current,
          value,
        };
      }
    },
    [
      attachmentId,
      viewOnly,
      isCommentView,
      blockUpdates,
      updateAttachment,
      onChangeAttachment,
    ]
  );

  const getUserData = useCallback(
    async () => ({
      ...userDetails,
      avatarURL: "",
    }),
    [_.get(userDetails, "name")]
  );

  const toggleNameModal = useCallback(() => {
    setShowNameModal(prev => !prev);
  }, []);

  const onClickEditTitle = () => {
    setIsRenameClicked(true);
    toggleNameModal();
  };

  const closeNameModal = useCallback(() => {
    setShowNameModal(false);
    setIsRenameClicked(false);
  }, []);

  const submitUpdatedToddleDoc = async () => {
    try {
      if (_.size(pendingUpdatesRef.current) > 0) {
        await Promise.allSettled([...pendingUpdatesRef.current]);
      }
      if (getToddleDocAttachmentDetails?.refetch && _.isFunction(onSubmit)) {
        const refetchResult = await getToddleDocAttachmentDetails.refetch();
        const refreshed =
          _.get(refetchResult, "data.node") ||
          _.get(getToddleDocAttachmentDetails, "node") ||
          _.get(refetchResult, "data.getToddleDocAttachmentDetails.node") ||
          null;
        onSubmit?.(refreshed);
        if (isAddWorkFromTeacher)
          updateEvaluationData({ isToddleDocSubmitted: true });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onUploadFile = useCallback(
    (result, args) => {
      const { attachment, file, ...others } = args;
      const mimeType = _.get(attachment, "mimeType", "");
      const _attachmentId =
        attachmentInfoRef.current?.attachmentId || attachmentId;
      const _attachmentValue =
        attachmentInfoRef.current?.attachmentValue || attachmentValue;

      const uploadData = {
        url: result,
        name: _.get(file, "name", ""),
        mimeType: mimeType,
        type: getFileType(mimeType)?.text,
        metadata: {
          size: _.get(file, "size", 0),
          fileExtension: _.get(attachment, "metadata.fileExtension", ""),
        },
        parentType: "TODDLE_DOC_ATTACHMENT",
        parentId: _attachmentId,
        createdBy: _.get(userDetails, "id", ""),
      };
      if (_attachmentId) {
        const input = {
          id: _attachmentId,
          toddleDoc: {
            value: _attachmentValue,
            childAttachments: [uploadData],
          },
        };

        if (onChangeAttachment) {
          onChangeAttachment(input);
        } else {
          const promise = Promise.resolve(updateAttachment(input));
          pendingUpdatesRef.current?.add(promise);
          promise.finally(() => pendingUpdatesRef.current?.delete(promise));
        }
      }
    },
    [
      attachmentId,
      attachmentValue,
      onChangeAttachment,
      updateAttachment,
      userDetails?.id,
    ]
  );

  const onDocumentNameSubmit = useCallback(
    async ({ name, isRenameClicked = false }) => {
      await updateAttachment({
        id: attachmentId,
        name,
      });
      if (closeOnSubmit && !isRenameClicked) {
        onClose({ isSubmitted: true });
        await submitUpdatedToddleDoc();
      }
      toggleNameModal();
    },
    [updateAttachment, closeOnSubmit, attachmentId, toggleNameModal]
  );

  const onDisplayedCommentsLengthChange = useCallback(
    _.debounce(commentsArray => {
      setDisplayedCommentsLength(_.size(commentsArray));
    }, 500),
    []
  );

  const onDoneClick = async () => {
    onClose({ isSubmitted: true });
    await submitUpdatedToddleDoc();
  };

  const onEditDocument = () => {
    setDerivedMode(EDIT);
  };

  const handleUpdateFilters = updatedFilters => {
    setCommentFilters(_.get(updatedFilters, "key", "ACTIVE"));
  };

  const updatedValue = useMemo(() => {
    let val;
    if (showControlledEditMode && _.includes([VIEW, EDIT], derivedMode)) {
      val = _.get(toddleDocConfig.current, "value", attachmentValue);
    } else {
      val = !_.isEmpty(attachmentValue)
        ? attachmentValue
        : showLoading
        ? null
        : "<p></p>";
    }
    return val;
  }, [attachmentValue, toddleDocConfig.current, showLoading, derivedMode]);

  const labelValueMap = getLabValueMap(t);

  const extendedToolbarButtons = useMemo(() => {
    return showFiltersForEditMode && isCommentViewAndNewUIEnabled ? (
      <Dropdown
        overlay={
          <DropdownMenu
            dsVersion="2.0"
            options={getFilterDropdownOptions(t)}
            onClick={handleUpdateFilters}
            value={commentFilters}
            selectionType="radio"
            showSelection
          />
        }
      >
        <FilterLabelButton
          dsVersion="2.0"
          size="medium"
          label={t("common:comments")}
          labelValue={labelValueMap[commentFilters]}
          variant="neutral"
          type="outlined"
        />
      </Dropdown>
    ) : null;
  }, [showFiltersForEditMode, isCommentViewAndNewUIEnabled, commentFilters]);

  const commentUpdatesId = useMemo(() => {
    return `${_.size(comments)}-${
      prevConversationsHashRef.current
    }-${attachmentId}`;
  }, [comments, attachmentId, prevConversationsHashRef.current]);

  const effectiveDocEditorConfig = useMemo(() => {
    if (!isAddWorkFromTeacher) return docEditorConfig;
    return _.merge({}, docEditorConfig, {
      toolbar: { comments: { isEnabled: false } },
      floatingToolbar: { comments: { isEnabled: false } },
    });
  }, [isAddWorkFromTeacher, docEditorConfig]);

  const commentConfigs = useMemo(() => {
    const baseConfig = {
      commentPanelOffset: {
        base: 294,
        default: 30,
        active: 10,
        hovered: 25,
      },
    };

    if (isAddWorkFromTeacher) {
      return {
        isDisabled: true,
        isThreadResolveEnabled: false,
        isThreadDeleteEnabled: false,
        isThreadTypeChangeEnabled: false,
        ...baseConfig,
      };
    }

    const baseDisabledConfig = {
      isDisabled: !isCommentViewAndNewUIEnabled,
      isThreadDeleteEnabled: false,
      isThreadTypeChangeEnabled: false,
    };
    return _.get(userDetails, "userType") === "student" && mode === EDIT
      ? {
          isThreadResolveEnabled: true,
          ...baseDisabledConfig,
          ...baseConfig,
        }
      : _.get(userDetails, "userType") === "student"
      ? {
          isThreadResolveEnabled: false,
          ...baseDisabledConfig,
          ...baseConfig,
        }
      : {
          isDisabled: !isCommentViewAndNewUIEnabled,
          isThreadDeleteEnabled: true,
          isThreadTypeChangeEnabled: true,
          isThreadResolveEnabled: true,
          ...baseConfig,
        };
  }, [
    _.get(userDetails, "userType"),
    mode,
    isCommentViewAndNewUIEnabled,
    isAddWorkFromTeacher,
  ]);

  const onCommentListTypeChange = useCallback(type => {
    setCommentFilters(type);
  }, []);

  const showCommentFilters =
    isCommentViewAndNewUIEnabled &&
    !isAITutor &&
    (isEvaluation ||
      (_.get(userDetails, "userType") === "student" && isCommentView));

  const DocEditorComponent = (
    <DSProvider dsVersion="2.0">
      <DocEditorWithMemo
        className={classes.docEditorContainer}
        instanceId={instanceId}
        value={updatedValue}
        updateInputField={handleUpdateValue}
        saveDelay={300}
        containerStyle={_.merge(
          {},
          styles.docEditorContainerStyles,
          containerStyle
        )}
        width={width ? width : calculatedWidth}
        minHeight="100%"
        {...(!_.isEmpty(docEditorStyles)
          ? docEditorStyles
          : DEFAULT_EDITOR_STYLES)}
        config={effectiveDocEditorConfig}
        comments={comments}
        getUserData={getUserData}
        isCommentLoaded={isCommentsLoaded}
        waitForCommentsToLoad={waitForCommentsToLoad}
        isExtendedToolbarEnabled={showFiltersForEditMode}
        extendedToolbarButtons={extendedToolbarButtons}
        nodeConfig={_.merge({}, nodeConfig, nodeCofigFromProps)}
        isCommentView={isCommentView}
        viewOnly={isCommentView ? false : viewOnly}
        onUploadMedia={onUploadFile}
        isMathsEquationEnabled={true}
        isInitialContentChangeIgnored={isInitialContentChangeIgnored}
        nodesDisabled={_.merge({}, nodesDisabled, disabledNodes)}
        defaultTagSizes={defaultTagSizes}
        isTableSizeContained={true}
        tableMaxWidth={TABLE_MAXWIDTH}
        tableDefaultWidth={TABLE_MAXWIDTH}
        isNewCommentUIEnabled={false}
        commentListType={commentFilters}
        commentUpdateId={commentUpdatesId}
        onCommentArrayUpdate={onDisplayedCommentsLengthChange}
        commentConfig={commentConfigs}
        onCommentListTypeChange={onCommentListTypeChange}
        {...commentHandlers}
      />
    </DSProvider>
  );

  if ((viewOnly && !isAITutor) || isEvaluation) {
    return showLoading ? (
      <Loading />
    ) : (
      <div ref={docWrapperRef} className={classes.container}>
        {(derivedMode === VIEW && showControlledEditMode) ||
        showCommentFilters ? (
          <div className={classes.stickyHeaderWrapper}>
            <ToddleDocHeader
              onClickEdit={onEditDocument}
              showEditDocHeader={derivedMode === VIEW && isEvaluation}
              showCommentFilters={showCommentFilters}
              disableEdit={blockUpdates}
              onCommentFiltersChange={setCommentFilters}
              commentFilters={commentFilters}
            />
          </div>
        ) : null}
        <div className={classes.centerContainer}>{DocEditorComponent}</div>
      </div>
    );
  }

  return (
    <UIModal isOpen isFullScreen>
      {showLoading ? (
        <Loading />
      ) : (
        <div ref={docWrapperRef} className={classes.modalContentWrapper}>
          <ToddleDocHeader
            attachmentName={attachmentName}
            onBackClick={_.isFunction(onBackClick) ? onBackClick : onClose}
            onClickEdit={onClickEditTitle}
            onSubmit={onDoneClick}
            showEditTitle={!isEvaluation && mode !== VIEW && showEditTitle}
            showCommentFilters={showCommentFilters}
          />
          <div className={classes.centerContainer}>{DocEditorComponent}</div>
        </div>
      )}
      {showNameModal ? (
        <ToddleDocNameModal
          attachmentName={attachmentName}
          title={
            isRenameClicked
              ? t("classRoom:rename_document")
              : t("classRoom:name_your_document")
          }
          closeModal={closeNameModal}
          showSaveButton={isRenameClicked}
          onDocumentNameSubmit={onDocumentNameSubmit}
        />
      ) : null}
    </UIModal>
  );
};

ToddleDocComponent.defaultProps = {
  fetchPolicy: "cache-and-network",
};

const mapStateToProps = (state, ownProps) => {
  const {
    isAddWorkFromTeacher: isAddWorkFromTeacherProp,
    isAITutor,
  } = ownProps;
  const isJournal = _.includes(window.location.href, "/journal");
  const userInfo = _.get(state, "login.userInfo");
  const {
    first_name: firstName,
    id: userId,
    last_name: lastName,
    user_type: userType,
  } = userInfo;
  const isAddWorkFromTeacher = isAddWorkFromTeacherProp && userType === "staff";
  const userDetails = {
    name: `${firstName} ${lastName}`,
    id: userId,
    userType,
  };

  let attachmentId = "";
  if (!_.isEmpty(ownProps.createdToddleDocAttachment)) {
    attachmentId =
      _.get(ownProps, "createdToddleDocAttachment.[0].attachments.[0].id") ||
      _.get(ownProps, "createdToddleDocAttachment.id");
  } else {
    attachmentId =
      _.get(ownProps, "attachmentId") ||
      _.get(ownProps, "attachment.attachmentId") ||
      _.get(ownProps, "attachment.id", "");
  }

  const studentAssignmentId = _.get(ownProps, "params.studentAssignmentId");

  return {
    propsMode: "EDIT",
    attachmentId,
    isJournal,
    userDetails,
    studentAssignmentId,
    isLoading: _.isEmpty(attachmentId),
    commentsState: !isAITutor ? "ALL" : undefined,
    isAddWorkFromTeacher,
  };
};

const mapActionCreators = {
  updateAttachment,
  createToddleDocConversation,
  updateToddleDocConversation,
  deleteToddleDocConversation,
  updateEvaluationData,
  setToastMsg,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapActionCreators),
  graphql(getToddleDocAttachmentQuery, {
    skip: ({ attachmentId, isAITutor }) =>
      isAITutor || _.isEmpty(attachmentId) || _.includes(attachmentId, "NEW_"),
    name: "getToddleDocAttachmentDetails",
    options: ({ attachmentId, propsMode, commentsState, fetchPolicy }) => ({
      fetchPolicy,
      variables: {
        id: attachmentId,
        filters: {
          mode: propsMode,
        },
        state: commentsState,
      },
    }),
    props({
      getToddleDocAttachmentDetails,
      ownProps: { studentAssignmentId, isLoading },
    }) {
      const fetchedAttachment = _.get(
        getToddleDocAttachmentDetails,
        "node",
        {}
      );
      const threads = _.get(fetchedAttachment, "toddleDoc.conversations");
      const transformedConversations = mapBackendConversationsToFrontend(
        threads
      );

      const onPusherEventTriggered = async params => {
        // When AI evaluation is completed, refetch the toddle doc attachment details so that comments get updated (in student assignment scenario)
        if (
          params?.event === "completed" &&
          params?.studentAssignmentId === studentAssignmentId
        ) {
          await getToddleDocAttachmentDetails?.refetch();
        }
      };

      return {
        fetchedAttachment,
        conversations: transformedConversations,
        isData: !_.isEmpty(fetchedAttachment),
        onPusherEventTriggered,
        getToddleDocAttachmentDetails,
        networkStatus: getToddleDocAttachmentDetails?.networkStatus,
        isLoading:
          isLoading ||
          getToddleDocAttachmentDetails?.["networkStatus"] == 1 ||
          getToddleDocAttachmentDetails?.["networkStatus"] == 2,
      };
    },
  }),
  withPusherBind(["completed"])
)(ToddleDocComponent);

ToddleDocComponent.displayName = "ToddleDocComponent";
